import { Box, Stack, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { RHFTextField } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import WarehouseDemandLineRow from './WarehouseDemandLineRow';
import WarehouseDemandProductField from './WarehouseDemandProductField';
import {
  DEMAND_MAX_LINES,
  DEMAND_NOTE_MAX,
  DemandProductOption,
  DemandRequestFormValues,
  catalogueDemandLine,
  freeTextDemandLine,
} from './WarehouseDemandRequestForm.schema';

const f = (key: string): string => translateKey(`warehouse.demand.form.${key}`);

interface WarehouseDemandRequestFieldsProps {
  /** The dialog is open: the Product search may run. */
  isOpen: boolean;
  disabled: boolean;
}

/**
 * The lines of the request, added from one "Dodaj produkt" entry: a Warehouse
 * Product or, by the typed name, a product outside it; and one note for all
 * of them.
 */
export default function WarehouseDemandRequestFields({
  isOpen,
  disabled,
}: WarehouseDemandRequestFieldsProps): JSX.Element {
  const { control, getValues } = useFormContext<DemandRequestFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'lines' });
  const full = fields.length >= DEMAND_MAX_LINES;

  const addProduct = (product: DemandProductOption): void => {
    // One line per Warehouse Product: picking it again keeps that line.
    const listed = getValues('lines').some(
      (line) => line.product?.id === product.id,
    );
    if (!full && !listed) {
      append(catalogueDemandLine(product), { shouldFocus: false });
    }
  };

  const addOther = (name: string): void => {
    if (!full) {
      append(freeTextDemandLine(name), { shouldFocus: false });
    }
  };

  let linesMessage: string | null = null;
  if (full) {
    linesMessage = f('maxLines');
  } else if (fields.length === 0) {
    linesMessage = f('noLines');
  }

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <WarehouseDemandProductField
        enabled={isOpen}
        disabled={disabled || full}
        onPick={addProduct}
        onPickName={addOther}
      />
      {linesMessage && (
        <Typography variant="body2" color="text.secondary">
          {linesMessage}
        </Typography>
      )}
      {fields.length > 0 && (
        <Box>
          {fields.map((line, index) => (
            <WarehouseDemandLineRow
              key={line.id}
              index={index}
              line={line}
              disabled={disabled}
              onRemove={() => remove(index)}
            />
          ))}
        </Box>
      )}
      <RHFTextField
        name="note"
        label={f('note')}
        multiline
        minRows={2}
        disabled={disabled}
        inputProps={{
          maxLength: DEMAND_NOTE_MAX,
          'data-testid': 'warehouse-demand-note',
        }}
      />
    </Stack>
  );
}
